// LOT0E2_REGION_FIRST_PRP_TERRITORY_ASSIGNMENT_UI
import { useMemo, useState } from "react";
import { supabase } from "@/services/supabaseClient";

const QUEBEC_REGIONS = [
  "Abitibi-Témiscamingue",
  "Bas-Saint-Laurent",
  "Capitale-Nationale",
  "Centre-du-Québec",
  "Chaudière-Appalaches",
  "Côte-Nord",
  "Estrie",
  "Gaspésie–Îles-de-la-Madeleine",
  "Lanaudière",
  "Laurentides",
  "Laval",
  "Mauricie",
  "Montérégie",
  "Montréal",
  "Nord-du-Québec",
  "Outaouais",
  "Saguenay–Lac-Saint-Jean",
] as const;

type AssignmentResult = Record<string, unknown>;

function pretty(result: AssignmentResult | null): string {
  if (!result) {
    return "Aucune action exécutée.";
  }

  return JSON.stringify(result, null, 2);
}

export function PrpAdminTerritoryRegionAssignmentPanel() {
  const [prpEmail, setPrpEmail] =
    useState("testpr@forfeo.com");

  const [region, setRegion] =
    useState("Capitale-Nationale");

  const [busyAction, setBusyAction] =
    useState<string | null>(null);

  const [result, setResult] =
    useState<AssignmentResult | null>(null);

  const selectedRegion =
    useMemo(
      () =>
        QUEBEC_REGIONS.includes(region as typeof QUEBEC_REGIONS[number])
          ? region
          : "Capitale-Nationale",
      [
        region,
      ],
    );

  // LOT0E2B_DIRECT_FETCH_WITH_ERROR_BODY_V1
  const invoke =
    async (
      action:
        | "preview_region_assignment"
        | "assign_region_candidates_to_prp",
    ) => {
      setBusyAction(action);
      setResult(null);

      try {
        const {
          data: sessionData,
        } =
          await supabase.auth.getSession();

        const token =
          sessionData.session?.access_token;

        if (!token) {
          setResult({
            ok:
              false,
            action,
            error:
              "missing_session_token",
          });

          setBusyAction(null);
          return;
        }

        const supabaseUrl =
          (import.meta as any).env?.VITE_SUPABASE_URL as string | undefined;

        if (!supabaseUrl) {
          setResult({
            ok:
              false,
            action,
            error:
              "missing_vite_supabase_url",
          });

          setBusyAction(null);
          return;
        }

        const response =
          await fetch(
            `${supabaseUrl}/functions/v1/prp-territory-data-quality`,
            {
              method:
                "POST",
              headers: {
                Authorization:
                  `Bearer ${token}`,
                "Content-Type":
                  "application/json",
              },
              body:
                JSON.stringify({
                  action,
                  prp_email:
                    prpEmail.trim(),
                  region:
                    selectedRegion,
                }),
            },
          );

        const raw =
          await response.text();

        let parsed: AssignmentResult;

        try {
          parsed =
            raw
              ? JSON.parse(raw)
              : {};
        } catch {
          parsed = {
            raw,
          };
        }

        setResult({
          ok:
            response.ok && parsed.ok !== false,
          http_status:
            response.status,
          action,
          ...parsed,
        });
      } catch (error) {
        setResult({
          ok:
            false,
          action,
          error:
            error instanceof Error
              ? error.message
              : "region_assignment_request_failed",
        });
      }

      setBusyAction(null);
    };

  return (
    <section
      className="mt-8 rounded-[2rem] border border-cyan-200 bg-cyan-50 p-6 shadow-sm"
      data-lot0e2-region-first-prp-territory-assignment="true"
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.18em] text-cyan-700">
            Admin territoire PRP
          </p>

          <h2 className="mt-2 text-2xl font-black text-slate-950">
            Assigner les candidats par territoire
          </h2>

          <p className="mt-2 max-w-4xl text-sm font-bold text-cyan-900">
            Choisissez un PRP et une région administrative du Québec.
            Forfeo assigne les candidats approuvés de cette région au PRP.
            Aucune coordonnée manuelle n’est demandée.
          </p>
        </div>

        <span className="rounded-full border border-cyan-200 bg-white px-4 py-2 text-xs font-black uppercase tracking-[0.14em] text-cyan-700">
          Région-first
        </span>
      </div>

      <div className="mt-6 grid gap-4 xl:grid-cols-2">
        <label className="grid gap-2 text-xs font-black uppercase tracking-[0.12em] text-slate-500">
          PRP email
          <input
            className="rounded-2xl border border-cyan-100 bg-white px-4 py-3 text-sm font-bold normal-case tracking-normal text-slate-900 outline-none focus:border-cyan-400"
            value={prpEmail}
            onChange={(event) => setPrpEmail(event.target.value)}
          />
        </label>

        <label className="grid gap-2 text-xs font-black uppercase tracking-[0.12em] text-slate-500">
          Région administrative
          <select
            className="rounded-2xl border border-cyan-100 bg-white px-4 py-3 text-sm font-bold normal-case tracking-normal text-slate-900 outline-none focus:border-cyan-400"
            value={selectedRegion}
            onChange={(event) => setRegion(event.target.value)}
          >
            {QUEBEC_REGIONS.map((item) => (
              <option
                key={item}
                value={item}
              >
                {item}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="mt-6 flex flex-wrap gap-3">
        <button
          type="button"
          className="rounded-2xl border border-cyan-200 bg-white px-5 py-3 text-sm font-black text-cyan-800 shadow-sm disabled:cursor-not-allowed disabled:opacity-60"
          disabled={Boolean(busyAction) || !prpEmail.trim()}
          onClick={() => invoke("preview_region_assignment")}
        >
          {busyAction === "preview_region_assignment"
            ? "Prévisualisation..."
            : "Prévisualiser le territoire"}
        </button>

        <button
          type="button"
          className="rounded-2xl bg-slate-950 px-5 py-3 text-sm font-black text-white shadow-lg disabled:cursor-not-allowed disabled:opacity-60"
          disabled={Boolean(busyAction) || !prpEmail.trim()}
          onClick={() => invoke("assign_region_candidates_to_prp")}
        >
          {busyAction === "assign_region_candidates_to_prp"
            ? "Assignation..."
            : "Assigner candidats au PRP"}
        </button>
      </div>

      <p className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs font-black text-amber-800">
        Cette action rattache les commerces approuvés de la région au PRP.
        Les commerces sans géographie précise resteront dans “À localiser”.
        Aucun point de carte n’est inventé.
      </p>

      <pre className="mt-4 max-h-80 overflow-auto rounded-2xl bg-slate-950 p-4 text-xs font-bold text-cyan-50">
        {pretty(result)}
      </pre>
    </section>
  );
}
